
import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import ExpenseReportList from "@/components/ExpenseReportList";
import Sidebar from "@/components/Sidebar";
import Header from "@/components/Header";
import { Button } from "@/components/ui/button";
import { Plus } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { useHeaderData } from "@/hooks/use-header-data";

const ExpenseReportsPage = () => {
  const { organizationId } = useParams<{ organizationId: string }>();
  const navigate = useNavigate();
  const { user, organization, organizations } = useHeaderData();

  const { data: userRole } = useQuery({
    queryKey: ["user-role", organizationId],
    queryFn: async () => {
      if (!organizationId || !user) return null;
      
      const { data, error } = await supabase
        .from("organization_members")
        .select("role")
        .eq("organization_id", organizationId)
        .eq("user_id", user.id)
        .single();
      
      if (error) throw error;
      return data?.role;
    },
    enabled: !!organizationId && !!user
  });

  const isManager = userRole === 'admin' || userRole === 'manager';

  if (!user || !organizationId) {
    return null;
  }

  return (
    <div className="min-h-screen bg-background">
      <Header 
        user={user}
        organization={organization}
        organizations={organizations}
      />

      <div className="flex min-h-[calc(100vh-4rem)]">
        <Sidebar className="border-r border-border/50" />
        <main className={cn(
          "flex-1 container mx-auto px-4 py-8 space-y-8",
          "relative z-10 bg-background"
        )}>
          <div className="flex flex-col gap-2">
            <h1 className="text-3xl font-bold tracking-tight">Expense Reports</h1>
            <p className="text-muted-foreground">
              View and manage your expense reports
            </p>
          </div>

          <div className="flex justify-between items-center">
            <CardDescription className="text-sm text-muted-foreground">
              Track, submit and manage your business expenses
            </CardDescription>
            <Button onClick={() => navigate(`/app/${organizationId}/expense-reports/new`)}>
              <Plus className="h-4 w-4 mr-2" />
              Create New Report
            </Button>
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="text-xl">Your Reports</CardTitle>
              <CardDescription>
                Submit and track expense reports for approval
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ExpenseReportList organizationId={organizationId!} isManager={isManager} userId={user?.id} />
            </CardContent>
          </Card>
        </main>
      </div>
    </div>
  );
};

export default ExpenseReportsPage;
